import React, { useState } from "react";
import { Button, TextField, Modal, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { showCustomToast } from "../Misc/CustomToast";
import { getDownloadURL, getStorage, ref, uploadBytes, deleteObject } from "firebase/storage";
import UploaderDocForNote from "../Updloader/UploaderDocForNote";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";

function EditCBTNote(props) {
  const { currentUser } = useAuth();
  const [sessionDate, setSessionDate] = useState(
    props.sessionDateTime?.toDate ? props.sessionDateTime.toDate() : props.sessionDateTime || new Date()
  );
  const [sessionNumber, setSessionNumber] = useState(props.sessionNumber || "");
  const [presentation, setPresentation] = useState(props.presentation || "");
  const [treatmentGoal, setTreatmentGoal] = useState(props.treatmentGoal || "");
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState(props.fileUrls || []);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const filtered = selectedFiles.filter((f) => f.size <= 50 * 1024 * 1024);
    const rejected = selectedFiles.filter((f) => f.size > 50 * 1024 * 1024);
    if (rejected.length) {
      showCustomToast("Some files exceed 50MB and were skipped.", "error");
    }
    if (filtered.length) setHasChanged(true);
    setFiles((prev) => [...prev, ...filtered]);
  };

  const uploadFilesAndGetUrls = async (docId) => {
    const storage = getStorage();
    const fileUrls = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const prefix = props.clientId ? `clients/${props.clientId}` : "cbtNotes";
      const filePath = `Documents/${prefix}/${docId}/${file.name}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      fileUrls.push({ url: fileUrl, name: file.name });
    }
    return fileUrls;
  };

  const getStorageRefFromUrl = (url = "") => {
    try {
      const storage = getStorage();
      const path = decodeURIComponent(url.split("/o/")[1].split("?")[0]);
      return ref(storage, path);
    } catch {
      return null;
    }
  };

  const handleUpdate = async () => {
    if (!sessionDate || !sessionNumber || !presentation || !treatmentGoal) {
      showCustomToast("Please fill all mandatory fields.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const docRef = doc(db, "notes", props.noteId);
      const payload = {
        sessionDateTime: sessionDate,
        sessionNumber,
        presentation,
        treatmentGoal,
        updateAt: serverTimestamp(),
        doneBy: currentUser?.displayName || currentUser?.email || "Unknown",
        clientId: props.clientId || null,
        clientFirstName: props.clientFirstName || null,
        clientLastName: props.clientLastName || null,
        clientProfilePicture: props.clientProfilePicture || "",
      };
      let mergedFiles = existingFiles || [];
      if (files.length) {
        const newFiles = await uploadFilesAndGetUrls(props.noteId);
        mergedFiles = [...mergedFiles, ...newFiles];
        payload.fileUrls = mergedFiles;
      }
      await updateDoc(docRef, payload);
      showCustomToast("CBT note updated successfully.", "success");
      setIsLoading(false);
      props.closemodal();
    } catch (error) {
      console.error("Error updating CBT note:", error);
      showCustomToast("Error when updating CBT note", "error");
      setIsLoading(false);
    }
  };

  const getFileIcon = (name = "") => {
    const ext = name.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext)) return <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />;
    if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext))
      return <ImageIcon sx={{ color: "#1976d2" }} />;
    if (["mp3", "wav", "aac", "ogg", "m4a"].includes(ext))
      return <AudiotrackIcon sx={{ color: "#8e24aa" }} />;
    if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext))
      return <MovieCreationIcon sx={{ color: "#6d4c41" }} />;
    if (["zip", "rar", "7z"].includes(ext)) return <FolderZipIcon sx={{ color: "#f57c00" }} />;
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"].includes(ext))
      return <DescriptionIcon sx={{ color: "#2e7d32" }} />;
    return <InsertDriveFileIcon sx={{ color: "#607d8b" }} />;
  };

  return (
    <div style={{ width: "auto", height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="line1-modal-percent">
        <div className="modal-title">Edit CBT Note</div>
        <CloseIcon
          style={{
            marginRight: "16px",
            color: "rgb(214, 213, 213)",
            cursor: "pointer",
          }}
          onClick={props.closemodal}
        />
      </div>

      <div
        style={{
          width: "100%",
          marginTop: "0px",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "12px 16px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            background: "#f8f9fb",
            borderRadius: 12,
            padding: "16px 18px",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div style={{ minWidth: 180, maxWidth: 220 }}>
              <div className="label-textfield" style={{ marginBottom: 6 }}>
                Session number
              </div>
              <TextField
                value={sessionNumber}
                onChange={(e) => {
                  setSessionNumber(e.target.value);
                  setHasChanged(true);
                }}
                size="small"
                fullWidth
                style={{
                  borderRadius: "5px",
                  backgroundColor: "#f2eeef",
                }}
                placeholder="e.g. 3"
              />
            </div>
            <div style={{ minWidth: 240 }}>
              <DatePickerWithTimeComponent
                title="Session date & time"
                value={sessionDate}
                onChange={(d) => {
                  setSessionDate(d);
                  setHasChanged(true);
                }}
              />
            </div>
          </div>

          <div>
            <div className="label-textfield" style={{ marginBottom: 6 }}>
              Presentation
            </div>
            <TextField
              value={presentation}
              onChange={(e) => {
                setPresentation(e.target.value);
                setHasChanged(true);
              }}
              style={{
                width: "100%",
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
              }}
              placeholder="Describe presentation"
              multiline
              rows={4}
            />
          </div>
          <div>
            <div className="label-textfield" style={{ marginBottom: 6 }}>
              Treatment goal worked on
            </div>
            <TextField
              value={treatmentGoal}
              onChange={(e) => {
                setTreatmentGoal(e.target.value);
                setHasChanged(true);
              }}
              style={{
                width: "100%",
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
              }}
              placeholder="Describe treatment goal"
              multiline
              rows={4}
            />
          </div>
          <div
            style={{
              background: "#fff",
              border: "1px dashed #cbd5e1",
              borderRadius: 10,
              padding: 12,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Attachments</div>
            {existingFiles?.length ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                {existingFiles.map((f, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      background: "#f8fafc",
                    }}
                  >
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        color: "#1565c0",
                        textDecoration: "none",
                        flex: 1,
                      }}
                    >
                      {getFileIcon(f.name)}
                      <div style={{ fontSize: 13, color: "#111" }}>{f.name || "File"}</div>
                    </a>
                    <IconButton
                      size="small"
                      aria-label="delete"
                      onClick={() => {
                        setFileToDelete(f);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>No attachments</div>
            )}
            <UploaderDocForNote handleFileChange={handleFileChange} files={files} />
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
              Max file size: 50 MB each.
            </div>
          </div>
        </div>
      </div>
      <div
        className="line4-modal-percent"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "white", color: "black" }}
          onClick={props.closemodal}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={isLoading || !hasChanged}
          style={
            isLoading
              ? { backgroundColor: "red", color: "white" }
              : { backgroundColor: "#1565C0", color: "white" }
          }
        >
          {isLoading ? "UPDATING NOTE..." : "UPDATE NOTE"}
        </Button>
      </div>

      <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: 360,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Remove this attachment?</div>
          <div style={{ fontSize: 14, color: "#555", marginBottom: 16 }}>
            {fileToDelete?.name}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button variant="outlined" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                if (!fileToDelete) return;
                const updated = existingFiles.filter((f) => f.url !== fileToDelete.url);
                try {
                  const docRef = doc(db, "notes", props.noteId);
                  await updateDoc(docRef, { fileUrls: updated, updateAt: serverTimestamp() });
                  const refFrom = getStorageRefFromUrl(fileToDelete.url);
                  if (refFrom) {
                    try {
                      await deleteObject(refFrom);
                    } catch (e) {
                      console.warn("Failed to delete file from storage:", e);
                    }
                  }
                  setExistingFiles(updated);
                  setHasChanged(true);
                } catch (e) {
                  console.error("Failed to delete attachment:", e);
                  showCustomToast("Error removing attachment", "error");
                } finally {
                  setShowDeleteConfirm(false);
                  setFileToDelete(null);
                }
              }}
            >
              Delete
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default EditCBTNote;
